/**
 * Two short notes when somebody joins or leaves the call — rising to come in,
 * falling to go out.
 *
 * The sound is synthesised, not played from a file, and that is a decision
 * rather than a shortcut. Discord's own sounds are protected, and shipping a
 * third-party sample would pull its licence into this repository to say what
 * a sine wave and an envelope already say. Nothing is downloaded, nothing is
 * bundled, and the whole instrument is the four Web Audio calls below.
 *
 * The module is split so that the part with a rule in it — whether an event
 * becomes a sound at all — is a pure function, testable without an
 * `AudioContext`, which jsdom does not have and a headless run would never
 * exercise anyway.
 */

export type VoiceChimeKind = 'join' | 'leave';

// ---------------------------------------------------------------------------
// The decision
// ---------------------------------------------------------------------------

/**
 * How long one chime speaks for everything that happens next to it.
 *
 * Five people joining at the same second is one event to a listener, not
 * five, and five overlapping chimes are not five announcements — they are
 * noise. Anything arriving inside the window is absorbed by the chime that
 * already played, never queued to play afterwards: a queue would turn a burst
 * into a longer burst, which is the problem it was meant to solve.
 */
export const CHIME_WINDOW_MS = 400;

/** Everything the decision below needs, and nothing that needs a browser. */
export interface ChimeEvent {
  /** Whether this session wants the sounds at all. */
  enabled: boolean;
  /** True when the participant is this session's own. */
  /**
   * Kept in the shape for callers that still pass it, and deliberately
   * ignored. Hearing your own arrival is the point: it is the only signal that
   * tells you a drop-and-reconnect happened while you were not looking at the
   * screen — which is exactly when a voice channel is being used.
   */
  isLocal?: boolean;
  /** When the event arrived, on any monotonic-enough clock. */
  now: number;
  /** When a chime last actually played, or `null` if none has. */
  lastPlayedAt: number | null;
}

/**
 * Whether this arrival or departure is worth a sound.
 *
 * Three rules, in the order they cost the least to answer: the preference, the
 * participant being ourselves, and the window. The local guard is belt and
 * braces — LiveKit only raises `ParticipantConnected` for remote participants,
 * and never for the ones already in the room when we arrive — but the rule
 * "you do not get announced to yourself" is ours to state, not the library's,
 * and stating it here is what makes it hold if the event source ever changes.
 */
export function shouldPlayChime({ enabled, now, lastPlayedAt }: ChimeEvent): boolean {
  if (!enabled) {
    return false;
  }

  return lastPlayedAt === null || now - lastPlayedAt >= CHIME_WINDOW_MS;
}

// ---------------------------------------------------------------------------
// The preference
// ---------------------------------------------------------------------------

export const CHIME_STORAGE_KEY = 'discordo:voice-chime';

/**
 * On unless this browser was told otherwise.
 *
 * The reads and writes are wrapped because storage access *throws* in a
 * private window rather than coming back empty — and losing the preference has
 * to cost the preference, never the call.
 */
export function readChimePreference(): boolean {
  try {
    return window.localStorage.getItem(CHIME_STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function writeChimePreference(enabled: boolean): void {
  try {
    window.localStorage.setItem(CHIME_STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // Remembering the choice is a convenience; failing to must not be felt.
  }
}

// ---------------------------------------------------------------------------
// The instrument
// ---------------------------------------------------------------------------

/**
 * The sound file, served by the app rather than synthesised.
 *
 * The two-note chime this replaced was a stand-in chosen to avoid dragging a
 * licence into the project; the owner picked this one, so the stand-in is gone
 * rather than kept as a second code path nobody would exercise.
 */
const CHIME_URL = '/sounds/teleport.mp3';

/** Quiet enough to sit under a conversation instead of interrupting it. */
const VOLUME = 0.45;

/**
 * One decoded copy for the tab.
 *
 * Decoding on every arrival would stutter the audio thread, and a fresh
 * `Audio` per chime leaks a media element each time. Cloning the primed one is
 * what lets two arrivals overlap without either being cut short.
 */
let primed: HTMLAudioElement | null = null;

function element(): HTMLAudioElement | null {
  if (primed !== null) {
    return primed;
  }

  // Typed as optional because jsdom and old browsers are two places where the
  // constructor `lib.dom` promises is simply absent.
  const Constructor = (window as { Audio?: typeof Audio }).Audio;
  if (Constructor === undefined) {
    return null;
  }

  try {
    primed = new Constructor(CHIME_URL);
    primed.volume = VOLUME;
    primed.preload = 'auto';
  } catch {
    return null;
  }

  return primed;
}

/**
 * Loads the sound from the click that enters the call, and from nowhere else.
 *
 * It has to be the gesture. Audio started outside one is refused by the
 * browser's autoplay policy — silently, with no error and nothing in the
 * console, which looks exactly like a feature that was never wired up. Priming
 * here means the first arrival is heard instead of swallowed.
 */
export function primeVoiceChime(): void {
  const audio = element();

  if (audio === null) {
    return;
  }

  audio.load();
}

export function playVoiceChime(_kind: VoiceChimeKind): void {
  const audio = element();

  if (audio === null) {
    return;
  }

  // A clone rather than the original: two people arriving together would
  // otherwise restart the same element and cut the first sound in half.
  const voice = audio.cloneNode() as HTMLAudioElement;
  voice.volume = VOLUME;

  // A refused play is the browser saying "not from a gesture". The next
  // arrival gets another chance, and swallowing it only avoids an unhandled
  // rejection in the console.
  void voice.play().catch(() => undefined);
}
